import { describeThrown } from './describeThrown'

/**
 * The slice of `@stripe/stripe-react-native` the onramp client uses. A type-only
 * reference, so it costs nothing at runtime and typechecks against the dev copy.
 */
export type StripeSdk = Pick<
  typeof import('@stripe/stripe-react-native'),
  'initStripe' | 'isPlatformPaySupported' | 'useOnramp'
>

const INSTALL_STEPS =
  'Install `@stripe/stripe-react-native` in your app and enable its Onramp ' +
  'module: Expo — the config plugin with `includeOnramp: true`; bare iOS — add ' +
  "'stripe-react-native/Onramp' to your Podfile; bare Android — set " +
  "'StripeSdk_includeOnramp=true' in gradle.properties. Then rebuild the " +
  'native binary. Stripe cannot run in Expo Go.'

function unavailable(reason: string): Error {
  return new Error(
    `@funkit/connect-rn: \`stripeOnramp\` is configured but Stripe's SDK could not be used. ${INSTALL_STEPS} Underlying error: ${reason}`,
  )
}

/**
 * A resolved package that lacks the surface this client binds is as unusable as
 * a missing one, and the peer range cannot enforce it (npm only warns).
 * Checked here so the bridge downgrades it to a logged error instead of a
 * render-time crash at the app root.
 */
function assertShape(sdk: Partial<StripeSdk>): StripeSdk {
  const missing = (
    ['initStripe', 'isPlatformPaySupported', 'useOnramp'] as const
  ).filter((name) => typeof sdk[name] !== 'function')
  if (missing.length > 0) {
    throw unavailable(`installed version exports no ${missing.join(', ')}`)
  }
  return sdk as StripeSdk
}

/**
 * Loads Stripe's SDK on demand. It is an optional peer: a host that never
 * configures Stripe never installs it, so nothing may name the package outside
 * the guarded `import()` below.
 *
 * - Metro treats an `import()` inside a syntactic `try` as optional, so a bundle
 *   still builds when the package is absent. This function is deliberately not
 *   `async`: a regenerator pass would flatten the `try` and lose that marker.
 * - The SDK calls `TurboModuleRegistry.getEnforcing('StripeSdk')` as its module
 *   body runs, which throws on a binary without the pod. Deferring the import
 *   to here keeps that throw out of every host's startup path.
 */
export function loadStripeSdk(): Promise<StripeSdk> {
  try {
    return import('@stripe/stripe-react-native').then(assertShape, (error) => {
      throw unavailable(describeThrown(error))
    })
  } catch (error) {
    return Promise.reject(unavailable(describeThrown(error)))
  }
}
